import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Search, RefreshCw, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import img1 from '../../../assets/attendenceCarousel.png';
import { attendanceApi } from '../services/api';
import DateRangeSelector from '../components/analytics/DateRangeSelector';

// ─── Types ───────────────────────────────────────────────────────────────────

type EmpType = 'permanent' | 'contractual';

interface DateSelection {
  year: number;
  month?: number;
  date?: number;
  useLatest?: boolean;
}

interface EmployeeDailyRow {
  empId: string | number;
  empName: string;
  empType: 'Permanent' | 'Contractual';
  kvId: string | number;
  kvName: string;
  roId: string | number;
  roName: string;
  status: 'P' | 'A' | 'H' | 'NM' | 'WE';
}

// ─── Constants ───────────────────────────────────────────────────────────────

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const STATUS_META: Record<string, { label: string; bg: string; text: string; full: string }> = {
  P:  { label: 'P',  bg: 'bg-green-100 hover:bg-green-200',  text: 'text-green-700',  full: 'Present' },
  A:  { label: 'A',  bg: 'bg-red-100 hover:bg-red-200',      text: 'text-red-700',    full: 'Absent' },
  H:  { label: 'H',  bg: 'bg-cyan-100 hover:bg-cyan-200',    text: 'text-cyan-700',   full: 'Holiday' },
  NM: { label: 'NM', bg: 'bg-yellow-100 hover:bg-yellow-200', text: 'text-yellow-700', full: 'Not Marked' },
  WE: { label: 'WE', bg: 'bg-blue-100 hover:bg-blue-200',    text: 'text-blue-500',   full: 'Weekend' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isWeekend(year: number, month: number, day: number): boolean {
  const dow = new Date(year, month - 1, day).getDay();
  return dow === 0 || dow === 6;
}

function mapDailyRow(raw: any, empType: EmpType, year: number, month: number, date: number): EmployeeDailyRow {
  const details = raw.details ?? raw;
  const attendance = raw.attendance ?? details.attendance ?? raw;
  
  // Status mapping logic
  let status: 'P' | 'A' | 'H' | 'NM' | 'WE' = 'NM';
  const rawStatus = String(raw.status ?? attendance.status ?? '').toUpperCase();
  const calendarStatus = raw.calendar?.status ?? attendance.calendar?.status ?? 11;

  if (calendarStatus === 12 || calendarStatus === 13) {
    status = 'WE';
  } else if (calendarStatus !== 11) {
    status = 'H';
  } else if (rawStatus === '1' || rawStatus === 'P' || rawStatus === 'PRESENT' || rawStatus === '0.5') {
    status = 'P';
  } else if (rawStatus === '0' || rawStatus === 'A' || rawStatus === 'ABSENT') {
    status = 'A';
  } else {
    if (isWeekend(year, month, date)) {
      status = 'WE';
    } else {
      status = 'NM';
    }
  }

  const kvId = details.kvID ?? details.kvid ?? details.kvId ?? raw.kvID ?? raw.kvid ?? raw.kvId ?? '—';
  const roId = details.roID ?? details.roid ?? details.roId ?? raw.roID ?? raw.roid ?? raw.roId ?? '—';

  return {
    empId: details.emp_code ?? details.empCode ?? details.empId ?? raw.emp_code ?? raw.empCode ?? raw.empID ?? raw.empId ?? raw.empid ?? raw.id ?? '—',
    empName: details.emp_name ?? details.empName ?? raw.emp_name ?? raw.empName ?? raw.name ?? '—',
    empType: empType === 'permanent' ? 'Permanent' : 'Contractual',
    kvId,
    kvName: details.kvSchoolName || details.kvName || raw.kvName || raw.schoolName || '—',
    roId,
    roName: details.roName ?? details.kvRoName ?? raw.roName ?? '—',
    status,
  };
}

/** Export visible rows to CSV */
function exportCSV(employees: EmployeeDailyRow[], year: number, month: number, date: number) {
  const headers = [
    'Emp ID', 'Employee Name', 'Type', 'KV ID', 'KV Name', 'RO ID', 'RO', 'Status'
  ];

  const rows = employees.map(emp => [
    emp.empId, emp.empName, emp.empType, emp.kvId, emp.kvName,
    emp.roId, emp.roName, STATUS_META[emp.status]?.full ?? ''
  ]);

  const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const dateStr = `${String(date).padStart(2, '0')}-${MONTHS[month - 1]}-${year}`;
  a.href = url;
  a.download = `staff-daily-attendance-${dateStr}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function StaffDailyAttendance() {
  const navigate = useNavigate();
  const carouselImages = [img1];

  const now = new Date();
  const [dateSelection, setDateSelection] = useState<DateSelection>({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    date: now.getDate(),
    useLatest: false,
  });

  const selectedYear = dateSelection.year;
  const selectedMonth = dateSelection.month ?? (now.getMonth() + 1);
  const selectedDate = dateSelection.date ?? now.getDate();
  const dateDependencyKey = `${selectedYear}-${selectedMonth}-${selectedDate}-${dateSelection.useLatest}`;
  const [empType, setEmpType] = useState<EmpType>('permanent');
  const [search, setSearch] = useState('');
  
  // Separate states for marked and unmarked lists
  const [markedEmployees, setMarkedEmployees] = useState<EmployeeDailyRow[]>([]);
  const [unmarkedEmployees, setUnmarkedEmployees] = useState<EmployeeDailyRow[]>([]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiSummary, setApiSummary] = useState<any>(null);

  // View filter dropdown state
  const [viewFilter, setViewFilter] = useState<'all' | 'marked' | 'unmarked'>('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalMarkedRecords, setTotalMarkedRecords] = useState(0);
  const [totalUnmarkedRecords, setTotalUnmarkedRecords] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [jumpPage, setJumpPage] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState<{ current: number; total: number } | null>(null);

  // Two-digit year the API expects (2025 → 25)
  const apiYear = selectedYear % 100;

  const fetchData = async (page: number = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await attendanceApi.getStaffDailyAttendance(
        page,
        apiYear,
        selectedMonth,
        selectedDate,
        empType === 'permanent' ? 'regular' : 'contractual'
      );

      if (!res) {
        setError('No data returned from the server.');
        setMarkedEmployees([]);
        setUnmarkedEmployees([]);
        setTotalMarkedRecords(0);
        setTotalUnmarkedRecords(0);
        setApiSummary(null);
        return;
      }

      // Extract summary
      const summaryObj = res.summary ?? res.summaryEmpAttendance;
      setApiSummary(summaryObj ?? null);

      const rawList: any[] =
        Array.isArray(res.data) ? res.data :
        Array.isArray(res.employees) ? res.employees :
        Array.isArray(res.records) ? res.records :
        Array.isArray(res) ? res :
        [];

      const notMarkedRawList: any[] =
        Array.isArray(res.notMarkedAttendanceEMployee) ? res.notMarkedAttendanceEMployee :
        Array.isArray(res.notMarkedAttendanceEmployee) ? res.notMarkedAttendanceEmployee :
        [];

      // Determine page size (limit) dynamically, fallback to 100
      const limit = res.pagination?.limit ?? (rawList.length > 0 ? rawList.length : 100);
      setPageSize(limit);

      // Determine total marked records
      const totalPresent = summaryObj ? Number(summaryObj.totalPresentReg ?? summaryObj.totalPresentCon ?? summaryObj.totalPresent ?? 0) : 0;
      const totalAbsent = summaryObj ? Number(summaryObj.totalAbsentReg ?? summaryObj.totalAbsentCon ?? summaryObj.totalAbsent ?? 0) : 0;
      const totalMarkedFromSummary = totalPresent + totalAbsent;
      const totalMarked = res.pagination?.total ?? res.total ?? (totalMarkedFromSummary || rawList.length);
      setTotalMarkedRecords(totalMarked);

      // Determine total unmarked records
      const totalUnmarked = summaryObj ? Number(summaryObj.totalNotMarked ?? summaryObj.totalNotMarkedReg ?? summaryObj.totalNotMarkedCon ?? 0) : 0;
      const totalUnmarkedFinal = totalUnmarked || notMarkedRawList.length;
      setTotalUnmarkedRecords(totalUnmarkedFinal);

      setCurrentPage(page);

      const mappedMarked = rawList.map(r => mapDailyRow(r, empType, selectedYear, selectedMonth, selectedDate));
      setMarkedEmployees(mappedMarked);

      const mappedUnmarked = notMarkedRawList.map(r => {
        const row = mapDailyRow(r, empType, selectedYear, selectedMonth, selectedDate);
        row.status = 'NM';
        return row;
      });
      setUnmarkedEmployees(mappedUnmarked);
    } catch {
      setError('Failed to load daily attendance data. Please try again.');
      setMarkedEmployees([]);
      setUnmarkedEmployees([]);
      setApiSummary(null);
    } finally {
      setLoading(false);
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateDependencyKey, empType, viewFilter]);

  // Computed values based on active viewFilter
  const displayedEmployees = useMemo(() => {
    if (viewFilter === 'marked') {
      return markedEmployees;
    }
    if (viewFilter === 'unmarked') {
      return unmarkedEmployees;
    }
    // 'all'
    return [...markedEmployees, ...unmarkedEmployees];
  }, [markedEmployees, unmarkedEmployees, viewFilter]);

  const activeTotalRecords = useMemo(() => {
    if (viewFilter === 'marked') return totalMarkedRecords;
    if (viewFilter === 'unmarked') return totalUnmarkedRecords;
    // 'all'
    return totalMarkedRecords + totalUnmarkedRecords;
  }, [viewFilter, totalMarkedRecords, totalUnmarkedRecords]);

  const activeTotalPages = useMemo(() => {
    const limit = pageSize || 100;
    if (viewFilter === 'marked') return Math.max(1, Math.ceil(totalMarkedRecords / limit));
    if (viewFilter === 'unmarked') return Math.max(1, Math.ceil(totalUnmarkedRecords / limit));
    // 'all' - lockstep pagination based on the larger dataset
    return Math.max(1, Math.ceil(Math.max(totalMarkedRecords, totalUnmarkedRecords) / limit));
  }, [viewFilter, totalMarkedRecords, totalUnmarkedRecords, pageSize]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > activeTotalPages || loading) return;
    fetchData(page);
  };

  const handleJumpToPage = () => {
    if (loading) return;
    const page = Number(jumpPage);
    if (!Number.isInteger(page) || page < 1 || page > activeTotalPages) return;
    setJumpPage('');
    handlePageChange(page);
  };

  const handleExportCSV = async () => {
    if (exportLoading) return;
    setExportLoading(true);
    setExportProgress({ current: 0, total: 1 });
    try {
      const getRawList = (res: any): any[] =>
        Array.isArray(res.data) ? res.data :
        Array.isArray(res.employees) ? res.employees :
        Array.isArray(res.records) ? res.records :
        Array.isArray(res) ? res : [];

      const first = await attendanceApi.getStaffDailyAttendance(1, apiYear, selectedMonth, selectedDate, empType === 'permanent' ? 'regular' : 'contractual');
      if (!first) return;

      const firstRaw = getRawList(first);
      const limit = first.pagination?.limit ?? (firstRaw.length > 0 ? firstRaw.length : 100);
      
      const summaryObj = first.summary ?? first.summaryEmpAttendance;
      const totalPresent = summaryObj ? Number(summaryObj.totalPresentReg ?? summaryObj.totalPresentCon ?? summaryObj.totalPresent ?? 0) : 0;
      const totalAbsent = summaryObj ? Number(summaryObj.totalAbsentReg ?? summaryObj.totalAbsentCon ?? summaryObj.totalAbsent ?? 0) : 0;
      const totalMarkedFromSummary = first.pagination?.total ?? first.total ?? (totalPresent + totalAbsent);
      const totalMarked = totalMarkedFromSummary || firstRaw.length;

      const totalUnmarked = summaryObj ? Number(summaryObj.totalNotMarked ?? summaryObj.totalNotMarkedReg ?? summaryObj.totalNotMarkedCon ?? 0) : 0;
      const totalUnmarkedFinal = totalUnmarked || (
        Array.isArray(first.notMarkedAttendanceEMployee) ? first.notMarkedAttendanceEMployee.length :
        Array.isArray(first.notMarkedAttendanceEmployee) ? first.notMarkedAttendanceEmployee.length : 0
      );

      let exportTotalPages = 1;
      if (viewFilter === 'marked') {
        exportTotalPages = Math.max(1, Math.ceil(totalMarked / limit));
      } else if (viewFilter === 'unmarked') {
        exportTotalPages = Math.max(1, Math.ceil(totalUnmarkedFinal / limit));
      } else {
        exportTotalPages = Math.max(1, Math.ceil(Math.max(totalMarked, totalUnmarkedFinal) / limit));
      }

      const getPageRows = (res: any) => {
        const markedRaw = getRawList(res);
        const unmarkedRaw = Array.isArray(res.notMarkedAttendanceEMployee) ? res.notMarkedAttendanceEMployee :
                            Array.isArray(res.notMarkedAttendanceEmployee) ? res.notMarkedAttendanceEmployee :
                            [];
        
        const markedMapped = markedRaw.map((r: any) => mapDailyRow(r, empType, selectedYear, selectedMonth, selectedDate));
        const unmarkedMapped = unmarkedRaw.map((r: any) => {
          const row = mapDailyRow(r, empType, selectedYear, selectedMonth, selectedDate);
          row.status = 'NM';
          return row;
        });

        if (viewFilter === 'marked') return markedMapped;
        if (viewFilter === 'unmarked') return unmarkedMapped;
        return [...markedMapped, ...unmarkedMapped];
      };

      let allRows: EmployeeDailyRow[] = getPageRows(first);
      setExportProgress({ current: 1, total: exportTotalPages });

      for (let p = 2; p <= exportTotalPages; p++) {
        const res = await attendanceApi.getStaffDailyAttendance(p, apiYear, selectedMonth, selectedDate, empType === 'permanent' ? 'regular' : 'contractual');
        if (res) {
          allRows = [...allRows, ...getPageRows(res)];
        }
        setExportProgress({ current: p, total: exportTotalPages });
      }

      // Deduplicate by empId
      const seen = new Set<string | number>();
      const uniqueRows = allRows.filter(row => {
        const key = row.empId;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      exportCSV(uniqueRows, selectedYear, selectedMonth, selectedDate);
    } finally {
      setExportLoading(false);
      setExportProgress(null);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return displayedEmployees;
    const q = search.trim().toLowerCase();
    return displayedEmployees.filter(
      e =>
        e.empName.toLowerCase().includes(q) ||
        String(e.empId).includes(q) ||
        String(e.kvId).toLowerCase().includes(q) ||
        e.kvName.toLowerCase().includes(q) ||
        String(e.roId).toLowerCase().includes(q) ||
        e.roName.toLowerCase().includes(q)
    );
  }, [displayedEmployees, search]);

  // Daily statistics
  const stats = useMemo(() => {
    if (apiSummary) {
      const present = Number(apiSummary.totalPresentReg ?? apiSummary.totalPresentCon ?? apiSummary.totalPresent ?? 0);
      const absent = Number(apiSummary.totalAbsentReg ?? apiSummary.totalAbsentCon ?? apiSummary.totalAbsent ?? 0);
      const notMarked = Number(apiSummary.totalNotMarked ?? apiSummary.totalNotMarkedReg ?? apiSummary.totalNotMarkedCon ?? 0);
      const total = Number(apiSummary.totalEnrollmentReg ?? apiSummary.totalEnrollmentCon ?? apiSummary.totalEnrollment ?? apiSummary.total ?? (totalMarkedRecords + totalUnmarkedRecords));
      const totalWorking = present + absent;
      const percent = total > 0 ? Math.round((present / total) * 100) : (totalWorking > 0 ? Math.round((present / totalWorking) * 100) : 0);
      
      return {
        total,
        present,
        absent,
        notMarked,
        percent,
      };
    }

    const presentCount = markedEmployees.filter(e => e.status === 'P').length;
    const absentCount = markedEmployees.filter(e => e.status === 'A').length;
    const notMarkedCount = unmarkedEmployees.length;
    const total = totalMarkedRecords + totalUnmarkedRecords || (presentCount + absentCount + notMarkedCount);
    const percent = total > 0 ? Math.round((presentCount / total) * 100) : 0;

    return {
      total,
      present: totalMarkedRecords || presentCount,
      absent: absentCount,
      notMarked: totalUnmarkedRecords || notMarkedCount,
      percent,
    };
  }, [markedEmployees, unmarkedEmployees, totalMarkedRecords, totalUnmarkedRecords, apiSummary]);

  const exportPct = exportProgress
    ? Math.round((exportProgress.current / exportProgress.total) * 100)
    : 0;



  const formattedDateStr = `${String(selectedDate).padStart(2, '0')} ${MONTHS[selectedMonth - 1]} ${selectedYear}`;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">

      {/* Export Progress Modal */}
      {exportLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-5 min-w-[340px] max-w-sm w-full border border-purple-100">
            <div className="flex items-center gap-3 text-[#6A1786]">
              <Download className="w-6 h-6 animate-bounce" />
              <span className="text-lg font-bold">Preparing Export</span>
            </div>
            <p className="text-sm text-gray-500 text-center">
              Fetching daily records for {formattedDateStr}&hellip;
            </p>
            {/* Progress Bar */}
            <div className="w-full">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>
                  {exportProgress
                    ? `Page ${exportProgress.current} of ${exportProgress.total}`
                    : 'Connecting…'}
                </span>
                <span className="font-semibold text-[#6A1786]">{exportPct}%</span>
              </div>
              <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#6A1786] to-purple-400 rounded-full transition-all duration-300"
                  style={{ width: `${exportPct}%` }}
                />
              </div>
            </div>
            <p className="text-xs text-gray-400">Please wait, do not close this window</p>
          </div>
        </div>
      )}
      <Header />
      <HeroCarousel images={carouselImages} />

      <div className="flex-1 w-full px-4 md:px-10 lg:px-20 py-6">
        {/* Breadcrumb */}
        <Breadcrumb />

        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mt-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/attendance')}
              className="p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:border-purple-300 transition-all shadow-sm"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#6A1786]">Staff Daily Attendance</h1>
              <p className="text-sm text-gray-500">Day-wise punch-in and roster details · {formattedDateStr}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchData(currentPage)}
              className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 hover:border-purple-300 transition-all text-sm shadow-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={handleExportCSV}
              disabled={exportLoading || activeTotalRecords === 0}
              className="inline-flex items-center gap-2 px-3 py-2 bg-[#6A1786] hover:bg-[#7a1f96] text-white rounded-lg shadow-md hover:shadow-lg transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {exportLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {exportLoading ? 'Exporting…' : 'Export CSV'}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-5">
          <div className="flex flex-wrap gap-4 items-end">
            
            {/* Date Picker */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Select Date</label>
              <DateRangeSelector
                value={dateSelection}
                onChange={setDateSelection}
                allowYearOnly={false}
                allowMonthOnly={false}
              />
            </div>

            {/* Employee Type */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Employee Type</label>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
                {(['permanent', 'contractual'] as EmpType[]).map(type => (
                  <button
                    key={type}
                    onClick={() => setEmpType(type)}
                    className={`px-4 py-2 capitalize transition font-semibold ${
                      empType === type
                        ? 'bg-[#6A1786] text-white shadow-inner'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Show Records dropdown filter */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Show Records</label>
              <select
                value={viewFilter}
                onChange={e => setViewFilter(e.target.value as 'all' | 'marked' | 'unmarked')}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-300 cursor-pointer hover:border-purple-300 transition-all shadow-sm h-[38px] min-w-[180px]"
              >
                <option value="all">All / Combined ({stats.total.toLocaleString()})</option>
                <option value="marked">Marked Only ({(stats.present + stats.absent).toLocaleString()})</option>
                <option value="unmarked">Not Marked Only ({stats.notMarked.toLocaleString()})</option>
              </select>
            </div>

            {/* Search */}
            <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Search (this page)</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Name, ID, KV, RO, Designation..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Summary strip */}
        <div className="flex flex-wrap gap-4 mb-4">
          {[
            { label: 'Total Records', value: stats.total.toLocaleString() },
            { label: 'Present', value: stats.present },
            { label: 'Not Marked', value: stats.notMarked },
            { label: 'Attendance Rate', value: stats.percent + '%' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4 flex flex-col min-w-[130px] flex-grow sm:flex-grow-0 hover:shadow transition-shadow">
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wider">{s.label}</span>
              <span className="text-2xl font-bold text-[#6A1786] mt-1">{s.value}</span>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mb-4">
          {Object.entries(STATUS_META).map(([key, meta]) => (
            <span key={key} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border border-gray-100 shadow-sm ${meta.bg} ${meta.text} transition-colors`}>
              <span className="font-extrabold">{meta.label}</span>
              <span className="font-normal text-gray-500">{meta.full}</span>
            </span>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-500 font-medium">
              <RefreshCw className="w-6 h-6 animate-spin mr-2 text-[#6A1786]" />
              Loading attendance data...
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500 gap-3">
              <Calendar className="w-10 h-10 text-gray-300" />
              <p className="text-sm">{error}</p>
              <button onClick={() => fetchData(1)} className="text-sm font-semibold text-[#6A1786] hover:underline hover:text-purple-800">Try again</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
              <Calendar className="w-10 h-10 text-gray-300" />
              <p className="text-sm font-medium">
                {search.trim() ? 'No matching records found for this search.' : 'No attendance records found for this day.'}
              </p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[600px]">
              <table className="w-full text-left border-collapse text-xs">
                <thead className="sticky top-0 z-20 bg-[#6A1786] text-white">
                  <tr>
                    <th className="py-3.5 px-4 font-bold whitespace-nowrap sticky left-0 z-30 bg-[#6A1786] w-24 min-w-[96px] shadow-[2px_0_5px_rgba(0,0,0,0.1)]">Emp ID</th>
                    <th className="py-3.5 px-4 font-bold whitespace-nowrap sticky left-24 z-30 bg-[#6A1786] min-w-[170px] shadow-[2px_0_5px_rgba(0,0,0,0.1)]">Employee Name</th>
                    <th className="py-3.5 px-4 font-bold whitespace-nowrap">KV ID</th>
                    <th className="py-3.5 px-4 font-bold whitespace-nowrap min-w-[150px]">KV Name</th>
                    <th className="py-3.5 px-4 font-bold whitespace-nowrap">RO ID</th>
                    <th className="py-3.5 px-4 font-bold whitespace-nowrap min-w-[110px]">RO</th>
                    <th className="py-3.5 px-4 font-bold whitespace-nowrap text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {filtered.map((emp, idx) => {
                    const statusMeta = STATUS_META[emp.status];
                    return (
                      <tr key={emp.empId || idx} className="hover:bg-purple-50/40 transition-colors">
                        <td className="py-3 px-4 text-gray-600 font-medium sticky left-0 bg-white z-10 w-24 min-w-[96px] border-r border-gray-100/50 shadow-[2px_0_5px_rgba(0,0,0,0.02)]">
                          {emp.empId || '—'}
                        </td>
                        <td
                          className="py-3 px-4 font-semibold text-gray-800 sticky left-24 bg-white z-10 cursor-pointer hover:text-[#6A1786] min-w-[170px] border-r border-gray-100/50 shadow-[2px_0_5px_rgba(0,0,0,0.02)]"
                          onClick={() => navigate(
                            empType === 'contractual'
                              ? `/attendance/employee/${emp.empId}?emptype=contractual`
                              : `/attendance/employee/${emp.empId}`,
                            { state: { employee: emp, empType } }
                          )}
                        >
                          {emp.empName}
                        </td>
                        <td className="py-3 px-4 text-gray-600 font-medium">{emp.kvId || '—'}</td>
                        <td className="py-3 px-4 text-gray-600 min-w-[150px]">{emp.kvName}</td>
                        <td className="py-3 px-4 text-gray-600 font-medium">{emp.roId || '—'}</td>
                        <td className="py-3 px-4 text-gray-600 min-w-[110px]">{emp.roName}</td>
                        
                        <td className="py-3 px-4 text-center">
                          {statusMeta ? (
                            <span className={`inline-block w-8 py-1 rounded font-bold text-[10px] shadow-sm text-center ${statusMeta.bg} ${statusMeta.text} transition-colors`}>
                              {statusMeta.label}
                            </span>
                          ) : (
                            <span className="inline-block w-8 py-1 rounded font-bold text-[10px] shadow-sm text-center bg-gray-100 text-gray-500">
                              {emp.status}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {activeTotalPages > 1 && !loading && !error && (
          <div className="flex items-center justify-between mt-4 px-1">
            <p className="text-sm text-gray-500">
              Page <span className="font-semibold text-[#6A1786]">{currentPage}</span> of{' '}
              <span className="font-semibold text-gray-700">{activeTotalPages}</span> &nbsp;·&nbsp;
              <span className="font-semibold text-gray-700">{activeTotalRecords.toLocaleString()}</span> total records
            </p>
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="px-2.5 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg bg-white text-gray-600 hover:bg-gray-50 hover:border-purple-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                First
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1.5 border border-gray-200 rounded-lg bg-white text-gray-600 hover:bg-gray-50 hover:border-purple-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from({ length: Math.min(5, activeTotalPages) }, (_, i) => {
                const startPage = Math.max(1, Math.min(currentPage - 2, activeTotalPages - 4));
                return startPage + i;
              }).map(p => (
                <button
                  key={p}
                  onClick={() => handlePageChange(p)}
                  className={`px-3 py-1.5 text-xs border rounded-lg transition-all font-semibold ${
                    p === currentPage
                      ? 'bg-[#6A1786] text-white border-[#6A1786] shadow-sm'
                      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:border-purple-300'
                  }`}
                >
                  {p}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === activeTotalPages}
                className="p-1.5 border border-gray-200 rounded-lg bg-white text-gray-600 hover:bg-gray-50 hover:border-purple-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => handlePageChange(activeTotalPages)}
                disabled={currentPage === activeTotalPages}
                className="px-2.5 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg bg-white text-gray-600 hover:bg-gray-50 hover:border-purple-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Last
              </button>
              
              <div className="flex items-center gap-2 px-1">
                <input
                  type="text"
                  value={jumpPage}
                  onChange={e => setJumpPage(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleJumpToPage();
                    }
                  }}
                  placeholder="Page"
                  className="w-14 px-2 py-1.5 text-xs border border-gray-200 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-purple-300 font-semibold"
                />
                <button
                  onClick={handleJumpToPage}
                  disabled={loading || !jumpPage}
                  className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg bg-white text-gray-600 hover:bg-gray-50 hover:border-purple-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  Go
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
